import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { SaveToast, useSaveToast } from './SaveToast';

const GOOGLE_FONT_MAP = {
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@300;400;500;600;700',
  'DM Sans': 'DM+Sans:wght@300;400;500;600;700',
  'Manrope': 'Manrope:wght@300;400;500;600;700',
  'Inter': 'Inter:wght@300;400;500;600;700',
  'Roboto': 'Roboto:wght@300;400;500;700',
  'Lato': 'Lato:wght@300;400;700',
  'Poppins': 'Poppins:wght@300;400;500;600;700',
  'Nunito': 'Nunito:wght@300;400;500;600;700',
  'Open Sans': 'Open+Sans:wght@300;400;500;600;700',
  'JetBrains Mono': 'JetBrains+Mono:wght@300;400;500;600;700',
};

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || !GOOGLE_FONT_MAP?.[fontFamily]) return;
  const id = `gfont-${fontFamily?.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_MAP?.[fontFamily]}&display=swap`;
  document.head?.appendChild(link);
};

const ColorPicker = ({ label, value, defaultValue, onChange, onClear, onApplyToAll, onClearAll, description = undefined }) => {
  const displayValue = value || defaultValue || '#000000';
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <div className="flex items-center gap-2">
          {onApplyToAll && (
            <button
              type="button"
              onClick={() => onApplyToAll(displayValue)}
              className="text-xs text-primary/70 hover:text-primary transition-colors flex items-center gap-1"
              title={`Apply this colour to all fields`}
            >
              <Icon name="Layers" size={11} />
              Apply to All
            </button>
          )}
          {onClearAll && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              title="Remove per-field overrides and restore inheritance from global theme"
            >
              <Icon name="Eraser" size={11} />
              Clear All
            </button>
          )}
          {onClear && value && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
              title="Clear override and restore auto-darkened accent colour"
            >
              <Icon name="X" size={11} />
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={displayValue?.startsWith('#') ? displayValue : defaultValue || '#000000'}
          onChange={(e) => onChange(e?.target?.value)}
          className="w-9 h-9 rounded-md border border-border cursor-pointer p-0.5 bg-background flex-shrink-0"
        />
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e?.target?.value)}
          placeholder={defaultValue || '#000000'}
          className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </div>
  );
};

const SectionHeader = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon name={icon} size={15} color="var(--color-primary)" />
    <h4 className="text-sm font-semibold text-foreground">{title}</h4>
  </div>
);

const DEFAULT_THEME = {
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '14',
  fontWeight: '400',
  labelColor: '#111827',
  accentColor: '#0e7572',
  inputBackground: '#ffffff',
  inputTextColor: '#111827',
  borderRadius: '6',
  backgroundType: 'solid',
  backgroundColor: '#ffffff',
  gradientColor1: '#f0fdf4',
  gradientColor2: '#dcfce7',
  gradientDirection: 'to bottom',
  backgroundImage: '',
  overlayOpacity: 0,
  overlayColor: '#000000',
};

// Element types that don't have input-style overrides — skip when applying colour to all
const SKIP_TYPES = new Set(['button', 'content', 'media', 'visual', 'layout']);

const GlobalThemeBuilder = ({ globalTheme = {}, onUpdateTheme, multiStepConfig, formElements, onUpdateAllElements }) => {
  const [bgType, setBgType] = useState(globalTheme?.backgroundType || 'solid');
  const { toasts, addToast, dismissToast } = useSaveToast(2500);

  const update = (key, value) => {
    onUpdateTheme?.({ ...globalTheme, [key]: value });
  };

  // Apply a fieldStyle colour key to every eligible element at once
  const handleApplyToAll = (fieldStyleKey) => (colorValue) => {
    if (!formElements?.length || !onUpdateAllElements) return;
    const updated = formElements?.map(el => {
      if (SKIP_TYPES?.has(el?.type)) return el;
      return { ...el, fieldStyle: { ...(el?.fieldStyle || {}), [fieldStyleKey]: colorValue } };
    });
    const count = updated?.filter((el, i) => el !== formElements?.[i])?.length;
    onUpdateAllElements(updated);
    addToast(`Applied to ${count} field${count !== 1 ? 's' : ''}`);
  };

  // Returns true when at least one eligible element has the given fieldStyle key set
  const hasFieldOverrides = (fieldStyleKey) =>
    (formElements || []).some(
      (el) => !SKIP_TYPES.has(el?.type) && el?.fieldStyle && fieldStyleKey in el.fieldStyle
    );

  // Remove a fieldStyle colour key from every element, restoring global-theme inheritance
  const handleClearAllOverrides = (fieldStyleKey) => () => {
    if (!formElements?.length || !onUpdateAllElements) return;
    const updated = formElements?.map(el => {
      if (!el?.fieldStyle || !(fieldStyleKey in el.fieldStyle)) return el;
      const { [fieldStyleKey]: _removed, ...rest } = el.fieldStyle;
      return { ...el, fieldStyle: rest };
    });
    const count = updated?.filter((el, i) => el !== formElements?.[i])?.length;
    if (count === 0) return; // nothing to clear — skip feedback
    onUpdateAllElements(updated);
    addToast(`Cleared overrides on ${count} field${count !== 1 ? 's' : ''}`);
  };

  const handleReset = () => {
    setBgType('solid');
    onUpdateTheme?.({
      ...DEFAULT_THEME,
      // Preserve the dismissed width so the mobile-overflow warning doesn't
      // reappear just because the creator reset unrelated theme settings.
      ...(globalTheme?.mobileWarningDismissedWidth != null
        ? { mobileWarningDismissedWidth: globalTheme.mobileWarningDismissedWidth }
        : {}),
    });
  };

  // Load font whenever fontFamily changes
  useEffect(() => {
    if (globalTheme?.fontFamily) {
      loadGoogleFont(globalTheme?.fontFamily);
    }
  }, [globalTheme?.fontFamily]);

  const handleBgTypeChange = (type) => {
    setBgType(type);
    update('backgroundType', type);
  };

  const fontFamilyOptions = [
    { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
    { value: 'DM Sans', label: 'DM Sans' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Inter', label: 'Inter' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'JetBrains Mono', label: 'JetBrains Mono' },
  ];

  const fontSizeOptions = [
    { value: '12', label: '12px' },
    { value: '13', label: '13px' },
    { value: '14', label: '14px' },
    { value: '15', label: '15px' },
    { value: '16', label: '16px' },
    { value: '18', label: '18px' },
  ];

  const fontWeightOptions = [
    { value: '300', label: 'Light' },
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' },
  ];

  const borderRadiusOptions = [
    { value: '0', label: 'None (0px)' },
    { value: '4', label: 'Small (4px)' },
    { value: '6', label: 'Medium (6px)' },
    { value: '8', label: 'Large (8px)' },
    { value: '12', label: 'XL (12px)' },
    { value: '16', label: '2XL (16px)' },
    { value: '9999', label: 'Full (pill)' },
  ];

  const gradientDirectionOptions = [
    { value: 'to bottom', label: 'Top → Bottom' },
    { value: 'to right', label: 'Left → Right' },
    { value: 'to bottom right', label: 'Diagonal ↘' },
    { value: 'to bottom left', label: 'Diagonal ↙' },
    { value: '135deg', label: '135°' },
  ];

  const darkenHex = (hex, amount = 40) => {
    const h = (hex || '#0e7572').replace('#', '');
    const num = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0xff) - amount);
    const b = Math.max(0, (num & 0xff) - amount);
    return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`;
  };

  const footerBarColor = globalTheme?.footerColor || darkenHex(globalTheme?.accentColor || '#0e7572', 40);

  const bgPreview = () => {
    if (bgType === 'gradient') {
      const dir = globalTheme?.gradientDirection || 'to bottom';
      const c1 = globalTheme?.gradientColor1 || '#f0fdf4';
      const c2 = globalTheme?.gradientColor2 || '#dcfce7';
      return `linear-gradient(${dir}, ${c1}, ${c2})`;
    }
    if (bgType === 'image') {
      return globalTheme?.backgroundImage
        ? `url(${globalTheme?.backgroundImage}) center/cover no-repeat`
        : 'var(--color-muted)';
    }
    return globalTheme?.backgroundColor || '#ffffff';
  };

  return (
    <div className="space-y-5">
      {/* Reset Button */}
      <div className="flex justify-end">
        <button
          onClick={handleReset}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-md hover:border-primary/60 hover:text-primary hover:bg-primary/5 transition-colors"
          title="Reset all theme settings to defaults"
        >
          <Icon name="RotateCcw" size={12} />
          Reset to Defaults
        </button>
      </div>
      {/* Live Mini Preview */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div
          className="h-20 flex items-center justify-center relative"
          style={{ background: bgPreview() }}
        >
          {bgType === 'image' && globalTheme?.overlayOpacity > 0 && (
            <div
              className="absolute inset-0"
              style={{
                backgroundColor: globalTheme?.overlayColor || '#000000',
                opacity: (globalTheme?.overlayOpacity || 0) / 100,
              }}
            />
          )}
          <div className="relative z-10 text-center px-4">
            <p
              className="font-semibold text-sm"
              style={{
                fontFamily: globalTheme?.fontFamily || 'inherit',
                color: globalTheme?.labelColor || '#111827',
              }}
            >
              Form Preview
            </p>
            <div
              className="mt-1.5 h-6 rounded flex items-center px-2"
              style={{
                borderRadius: `${globalTheme?.borderRadius || 6}px`,
                backgroundColor: globalTheme?.inputBackground || '#ffffff',
                border: `1px solid ${globalTheme?.accentColor || '#0e7572'}`,
                fontSize: '11px',
                color: globalTheme?.inputTextColor || '#6b7280',
              }}
            >
              Sample input field
            </div>
          </div>
        </div>
        {/* Footer bar strip — only shown when Footer Bar progress style is active */}
        {multiStepConfig?.enabled && multiStepConfig?.progressStyle === 'footer-bar' ? (
          <div
            className="h-6 flex items-center justify-center px-3"
            style={{ backgroundColor: footerBarColor }}
          >
            <p className="text-[9px] font-medium opacity-70" style={{ color: '#ffffff' }}>
              Footer bar
            </p>
          </div>
        ) : (
          <div className="h-6 flex items-center justify-center px-3 bg-muted/40 border-t border-border/40">
            <p className="text-[9px] text-muted-foreground italic">
              Only visible with Footer Bar progress style
            </p>
          </div>
        )}
        <div className="px-3 py-1.5 bg-muted/40 border-t border-border">
          <p className="text-[10px] text-muted-foreground text-center">Live theme preview</p>
        </div>
      </div>
      {/* Typography */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60">
        <SectionHeader title="Typography" icon="Type" />
        <div className="space-y-3">
          <Select
            label="Font Family"
            options={fontFamilyOptions}
            value={globalTheme?.fontFamily || 'Plus Jakarta Sans'}
            onChange={(v) => update('fontFamily', v)}
          />
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Base Size"
              options={fontSizeOptions}
              value={globalTheme?.fontSize || '14'}
              onChange={(v) => update('fontSize', v)}
            />
            <Select
              label="Weight"
              options={fontWeightOptions}
              value={globalTheme?.fontWeight || '400'}
              onChange={(v) => update('fontWeight', v)}
            />
          </div>
          <ColorPicker
            label="Label Color"
            value={globalTheme?.labelColor}
            defaultValue="#111827"
            onChange={(v) => update('labelColor', v)}
            onApplyToAll={handleApplyToAll('labelColor')}
            onClearAll={hasFieldOverrides('labelColor') ? handleClearAllOverrides('labelColor') : undefined}
            onClear={() => update('labelColor', undefined)}
          />
        </div>
      </div>
      {/* Accent & Input Colors */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60">
        <SectionHeader title="Colors" icon="Palette" />
        <div className="space-y-3">
          <ColorPicker
            label="Accent / Primary Color"
            value={globalTheme?.accentColor}
            defaultValue="#0e7572"
            onChange={(v) => update('accentColor', v)}
            onApplyToAll={handleApplyToAll('borderColor')}
            onClearAll={hasFieldOverrides('borderColor') ? handleClearAllOverrides('borderColor') : undefined}
            description="Used for borders, focus rings, and buttons"
          />
          <ColorPicker
            label="Input Background"
            value={globalTheme?.inputBackground}
            defaultValue="#ffffff"
            onChange={(v) => update('inputBackground', v)}
            onApplyToAll={handleApplyToAll('inputBg')}
            onClearAll={hasFieldOverrides('inputBg') ? handleClearAllOverrides('inputBg') : undefined}
            onClear={() => update('inputBackground', undefined)}
          />
          <ColorPicker
            label="Input Text Color"
            value={globalTheme?.inputTextColor}
            defaultValue="#111827"
            onChange={(v) => update('inputTextColor', v)}
            onApplyToAll={handleApplyToAll('textColor')}
            onClearAll={hasFieldOverrides('textColor') ? handleClearAllOverrides('textColor') : undefined}
            onClear={() => update('inputTextColor', undefined)}
          />
          <ColorPicker
            label="Footer Bar Color"
            value={globalTheme?.footerColor}
            defaultValue="#1e293b"
            onChange={(v) => update('footerColor', v)}
            onClear={() => update('footerColor', undefined)}
            description="Used for the footer bar in multi-step forms. Leave blank to auto-darken the accent colour."
          />
        </div>
      </div>
      {/* Form Width */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60">
        <SectionHeader title="Form Width" icon="ArrowLeftRight" />
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">
              Max width: {globalTheme?.formMaxWidth ? `${globalTheme.formMaxWidth}px` : '576px (default)'}
            </label>
            {globalTheme?.formMaxWidth && (
              <button
                type="button"
                onClick={() => update('formMaxWidth', undefined)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                title="Reset to default width"
              >
                <Icon name="X" size={11} />
                Reset
              </button>
            )}
          </div>
          <input
            type="range"
            min={320}
            max={960}
            step={8}
            value={globalTheme?.formMaxWidth ?? 576}
            onChange={(e) => update('formMaxWidth', Number(e?.target?.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: 'var(--color-primary)' }}
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>320px</span>
            <span>576px</span>
            <span>960px</span>
          </div>
          {(globalTheme?.formMaxWidth ?? 576) > 480 &&
            (globalTheme?.formMaxWidth ?? 576) !== globalTheme?.mobileWarningDismissedWidth && (
            <div className="flex items-start gap-1.5 mt-1.5 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md">
              <Icon name="AlertTriangle" size={12} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <span className="flex-1 text-[11px] text-amber-600 dark:text-amber-400">
                Widths above 480px may overflow on small screens. Mobile users could need to scroll horizontally.
              </span>
              <button
                type="button"
                onClick={() => update('mobileWarningDismissedWidth', globalTheme?.formMaxWidth ?? 576)}
                className="ml-1 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors shrink-0"
                title="Dismiss this warning"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Border Radius */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60">
        <SectionHeader title="Border Radius" icon="Square" />
        <Select
          label="Global Border Radius"
          options={borderRadiusOptions}
          value={String(globalTheme?.borderRadius ?? '6')}
          onChange={(v) => update('borderRadius', v)}
        />
        <div className="mt-3 flex gap-2">
          {['0', '6', '12', '9999']?.map((r) => (
            <button
              key={r}
              onClick={() => update('borderRadius', r)}
              title={`${r}px`}
              className={`flex-1 h-8 border-2 transition-colors ${
                String(globalTheme?.borderRadius ?? '6') === r
                  ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
              }`}
              style={{ borderRadius: r === '9999' ? '9999px' : `${r}px` }}
            />
          ))}
        </div>
      </div>
      {/* Background */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60">
        <SectionHeader title="Background" icon="Image" />
        <div className="flex gap-1.5 mb-3">
          {['solid', 'gradient', 'image']?.map((type) => (
            <button
              key={type}
              onClick={() => handleBgTypeChange(type)}
              className={`flex-1 py-1.5 text-xs font-medium rounded-md border transition-colors capitalize ${
                bgType === type
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:border-primary/50 text-muted-foreground'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {bgType === 'solid' && (
          <ColorPicker
            label="Background Color"
            value={globalTheme?.backgroundColor}
            defaultValue="#ffffff"
            onChange={(v) => update('backgroundColor', v)}
          />
        )}

        {bgType === 'gradient' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <ColorPicker
                label="Color 1"
                value={globalTheme?.gradientColor1}
                defaultValue="#f0fdf4"
                onChange={(v) => update('gradientColor1', v)}
              />
              <ColorPicker
                label="Color 2"
                value={globalTheme?.gradientColor2}
                defaultValue="#dcfce7"
                onChange={(v) => update('gradientColor2', v)}
              />
            </div>
            <Select
              label="Direction"
              options={gradientDirectionOptions}
              value={globalTheme?.gradientDirection || 'to bottom'}
              onChange={(v) => update('gradientDirection', v)}
            />
          </div>
        )}

        {bgType === 'image' && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Image URL</label>
              <input
                type="url"
                value={globalTheme?.backgroundImage || ''}
                onChange={(e) => update('backgroundImage', e?.target?.value)}
                placeholder="https://example.com/bg.jpg"
                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Overlay Opacity: {globalTheme?.overlayOpacity || 0}%
              </label>
              <input
                type="range"
                min="0"
                max="80"
                value={globalTheme?.overlayOpacity || 0}
                onChange={(e) => update('overlayOpacity', parseInt(e?.target?.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: 'var(--color-primary)' }}
              />
            </div>
            <ColorPicker
              label="Overlay Color"
              value={globalTheme?.overlayColor}
              defaultValue="#000000"
              onChange={(v) => update('overlayColor', v)}
            />
          </div>
        )}
      </div>
      <SaveToast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default GlobalThemeBuilder;
